/**
 * Integrytas Group - Base App (MOBILE)
 * Page: RPD.DESIGNER — mobile-native (READ-ONLY).
 *
 * Touch-first counterpart of src/pages/rpd/ReportDesignerPage.tsx. The desktop
 * screen is a full banded-report AUTHORING tool (drag canvas, property inspector,
 * add/remove bands & elements, live preview) — all of which stay on the desktop.
 * On mobile this surfaces the same definitions as a scannable, read-only catalogue:
 * a dense card list of saved BANDED_REPORT templates + a bottom-sheet detail that
 * shows each report's bands and elements. No authoring, so NOTHING here writes:
 * every access is a gateway read via apiDb; routed_writes is deliberately not
 * imported (§2 — there is simply no write path on this screen).
 *
 * Same data/logic layer as the desktop (§16): reads doc_template (kind
 * BANDED_REPORT) + its doc_report_group / doc_report_band / doc_report_element
 * rows, resolving the localized template title from doc_template_tl against the
 * ACTIVE locale (§8, §17.B4 — no raw id/code reaches render). Band, element and
 * output-format values are CHECK enums, so their labels come from localized
 * labelled constants via t() (§17.B11), never the stored literal. Y/N renders as
 * the m-pill status pill. It never re-hosts the desktop grid.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, FileText, Layers, Monitor } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { formatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ── CHECK-enum labelled constants (§17.B11): the stored value is the code; the
// localized label is the ONLY thing rendered in cells / pills / subtitles. ──
interface EnumOpt { value: string; labelKey: string; fallback: string }

const BAND_TYPES: EnumOpt[] = [
  { value: 'REPORT_HEADER', labelKey: 'rpd.band_type.REPORT_HEADER', fallback: 'Report Header' },
  { value: 'PAGE_HEADER', labelKey: 'rpd.band_type.PAGE_HEADER', fallback: 'Page Header' },
  { value: 'GROUP_HEADER', labelKey: 'rpd.band_type.GROUP_HEADER', fallback: 'Group Header' },
  { value: 'DETAIL', labelKey: 'rpd.band_type.DETAIL', fallback: 'Detail' },
  { value: 'GROUP_FOOTER', labelKey: 'rpd.band_type.GROUP_FOOTER', fallback: 'Group Footer' },
  { value: 'PAGE_FOOTER', labelKey: 'rpd.band_type.PAGE_FOOTER', fallback: 'Page Footer' },
  { value: 'REPORT_FOOTER', labelKey: 'rpd.band_type.REPORT_FOOTER', fallback: 'Report Footer' },
];

const ELEMENT_TYPES: EnumOpt[] = [
  { value: 'LABEL', labelKey: 'rpd.element_type.LABEL', fallback: 'Label' },
  { value: 'FIELD', labelKey: 'rpd.element_type.FIELD', fallback: 'Field' },
  { value: 'EXPRESSION', labelKey: 'rpd.element_type.EXPRESSION', fallback: 'Expression' },
  { value: 'AGGREGATE', labelKey: 'rpd.element_type.AGGREGATE', fallback: 'Aggregate' },
  { value: 'SYSFIELD', labelKey: 'rpd.element_type.SYSFIELD', fallback: 'System Field' },
  { value: 'IMAGE', labelKey: 'rpd.element_type.IMAGE', fallback: 'Image' },
  { value: 'LINE', labelKey: 'rpd.element_type.LINE', fallback: 'Line' },
  { value: 'RECT', labelKey: 'rpd.element_type.RECT', fallback: 'Rectangle' },
  { value: 'BARCODE', labelKey: 'rpd.element_type.BARCODE', fallback: 'Barcode' },
];

const OUTPUT_FORMATS: EnumOpt[] = [
  { value: 'PDF', labelKey: 'rpd.output.PDF', fallback: 'PDF' },
  { value: 'XLSX', labelKey: 'rpd.output.XLSX', fallback: 'Excel (XLSX)' },
  { value: 'CSV', labelKey: 'rpd.output.CSV', fallback: 'CSV' },
  { value: 'HTML', labelKey: 'rpd.output.HTML', fallback: 'HTML' },
];

const BANDED_REPORT = 'BANDED_REPORT';

type StatusFacet = 'all' | 'active' | 'inactive';

interface ReportRow {
  doc_template_id: number;
  internal_code: string;
  display_text: string;
  page_size_code: string | null;
  output_default: string;
  is_active: 'Y' | 'N';
  description: string | null;
  updated_at: string;
}

interface GroupRow { doc_report_group_id: number; group_level: number; tracking_field: string }
interface BandRow {
  doc_report_band_id: number;
  band_type: string;
  doc_report_group_id: number | null;
  height_pt: number;
  sort_order: number;
}
interface ElementRow {
  doc_report_element_id: number;
  doc_report_band_id: number;
  element_seq: number;
  element_type: string;
  field_ref: string | null;
  expression: string | null;
}
interface PageSizeInfo { page_size_code: string; width_pt: number; height_pt: number }
interface ReportDetail { groups: GroupRow[]; bands: BandRow[]; elements: ElementRow[] }

interface TlRow { locale_code: string; title: string | null }

export function MobileReportDesignerPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [pageSizes, setPageSizes] = useState<Record<string, PageSizeInfo>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<ReportRow | null>(null);
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Page-size dimensions are a best-effort lookup for the card subtitle; a
      // failure here must not blank the report list.
      const { data: ps } = await apiDb
        .from('doc_page_size')
        .select('page_size_code, width_pt, height_pt');
      const psMap: Record<string, PageSizeInfo> = {};
      for (const p of (ps ?? []) as PageSizeInfo[]) psMap[p.page_size_code] = p;
      setPageSizes(psMap);

      const { data, error: e } = await apiDb
        .from('doc_template')
        .select('doc_template_id, internal_code, name, page_size_code, output_default, is_active, description, updated_at, doc_template_tl ( locale_code, title )')
        .eq('template_kind', BANDED_REPORT)
        .order('internal_code');
      if (e) throw e;

      type RawReport = {
        doc_template_id: number;
        internal_code: string;
        name: string;
        page_size_code: string | null;
        output_default: string;
        is_active: 'Y' | 'N';
        description: string | null;
        updated_at: string;
        doc_template_tl?: TlRow[];
      };
      const rows: ReportRow[] = ((data ?? []) as RawReport[]).map((r) => {
        const tls = r.doc_template_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale && tl.title);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US' && tl.title);
        return {
          doc_template_id: r.doc_template_id,
          internal_code: r.internal_code,
          display_text: exact?.title ?? fb?.title ?? r.name ?? r.internal_code,
          page_size_code: r.page_size_code,
          output_default: r.output_default,
          is_active: r.is_active,
          description: r.description,
          updated_at: r.updated_at,
        };
      });
      setReports(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (the _tl title follows
  // it — §8). `t` is intentionally NOT a dependency (fresh identity each render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const selectReport = async (r: ReportRow) => {
    setSelected(r);
    setDetail(null);
    setDetailBusy(true);
    setError(null);
    try {
      const [g, b, el] = await Promise.all([
        apiDb.from('doc_report_group')
          .select('doc_report_group_id, group_level, tracking_field')
          .eq('doc_template_id', r.doc_template_id).order('group_level'),
        apiDb.from('doc_report_band')
          .select('doc_report_band_id, band_type, doc_report_group_id, height_pt, sort_order')
          .eq('doc_template_id', r.doc_template_id).order('sort_order'),
        apiDb.from('doc_report_element')
          .select('doc_report_element_id, doc_report_band_id, element_seq, element_type, field_ref, expression, doc_report_band!inner(doc_template_id)')
          .eq('doc_report_band.doc_template_id', r.doc_template_id).order('element_seq'),
      ]);
      if (g.error) throw g.error;
      if (b.error) throw b.error;
      if (el.error) throw el.error;
      setDetail({
        groups: (g.data ?? []) as GroupRow[],
        bands: (b.data ?? []) as BandRow[],
        elements: (el.data ?? []) as ElementRow[],
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDetailBusy(false);
    }
  };

  const closeSheet = () => { setSelected(null); setDetail(null); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return reports.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return r.display_text.toLowerCase().includes(q) || r.internal_code.toLowerCase().includes(q);
    });
  }, [reports, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.rpd.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const kpis = useMemo(() => {
    const active = reports.filter((r) => r.is_active === 'Y').length;
    const pdf = reports.filter((r) => r.output_default === 'PDF').length;
    return { total: reports.length, active, inactive: reports.length - active, pdf };
  }, [reports]);

  const enumLabel = (opts: EnumOpt[], v: string) => {
    const o = opts.find((x) => x.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };
  const bandLabel = (v: string) => enumLabel(BAND_TYPES, v);
  const elementLabel = (v: string) => enumLabel(ELEMENT_TYPES, v);
  const outputLabel = (v: string) => enumLabel(OUTPUT_FORMATS, v);

  const pageSizeText = (code: string | null) => {
    if (!code) return t('mobile.rpd.no_page_size', 'No page size');
    const ps = pageSizes[code];
    return ps ? `${code} · ${Math.round(ps.width_pt)}×${Math.round(ps.height_pt)} pt` : code;
  };

  // Resolve a group band's tracking-field so a raw group id never reaches render.
  const bandTitle = (b: BandRow) => {
    const base = bandLabel(b.band_type);
    if (b.doc_report_group_id == null || !detail) return base;
    const grp = detail.groups.find((g) => g.doc_report_group_id === b.doc_report_group_id);
    return grp ? `${base} · ${grp.tracking_field}` : base;
  };

  const elementsForBand = (bandId: number) =>
    detail ? detail.elements.filter((e) => e.doc_report_band_id === bandId) : [];

  // A compact, deduped list of element-type labels for a band's subtitle.
  const bandElementSummary = (bandId: number) => {
    const els = elementsForBand(bandId);
    const seen: string[] = [];
    for (const e of els) {
      const label = elementLabel(e.element_type);
      if (!seen.includes(label)) seen.push(label);
    }
    return seen.join(', ');
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.rpd.title', 'Report Designer')}
        description={t('mobile.rpd.description', 'Saved banded-report templates and their layout bands; author on desktop.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.total.toLocaleString(), label: t('mobile.rpd.stat.reports', 'Reports') },
          { value: kpis.active.toLocaleString(), label: t('common.boolean.active', 'Active') },
          { value: kpis.inactive.toLocaleString(), label: t('common.boolean.inactive', 'Inactive') },
          { value: kpis.pdf.toLocaleString(), label: t('mobile.rpd.stat.pdf', 'PDF') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.rpd.search', 'Search reports')}
          aria-label={t('mobile.rpd.search', 'Search reports')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.rpd.none', 'No reports match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.doc_template_id}
              lead={<FileText />}
              leadTone={r.is_active === 'Y' ? 'blue' : 'slate'}
              title={r.display_text}
              subtitle={`${r.internal_code} · ${pageSizeText(r.page_size_code)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => void selectReport(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={closeSheet} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.rpd.detail', 'Report detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><FileText size={20} /></span>
              <span>
                <div className="m-nm">{selected.display_text}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.rpd.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.rpd.page_size', 'Page size')} value={pageSizeText(selected.page_size_code)} />
              <MobileDetailRow label={t('mobile.rpd.output', 'Output')} value={outputLabel(selected.output_default)} />
              <MobileDetailRow label={t('mobile.rpd.bands', 'Bands')} value={detail ? detail.bands.length.toLocaleString() : '—'} />
              <MobileDetailRow label={t('mobile.rpd.elements', 'Elements')} value={detail ? detail.elements.length.toLocaleString() : '—'} />
              <MobileDetailRow label={t('mobile.rpd.updated', 'Updated')} value={formatDateTime(selected.updated_at, activeLocale)} />
            </MobileContentCard>

            {detailBusy ? (
              <MobileContentCard><div className="m-empty">{t('common.loading', 'Loading…')}</div></MobileContentCard>
            ) : detail && detail.bands.length > 0 ? (
              <>
                <div className="m-sect"><h2>{t('mobile.rpd.layout', 'Layout bands')}</h2></div>
                <MobileContentCard list>
                  {detail.bands.map((b) => {
                    const count = elementsForBand(b.doc_report_band_id).length;
                    const summary = bandElementSummary(b.doc_report_band_id);
                    return (
                      <MobileCardRow
                        key={b.doc_report_band_id}
                        lead={<Layers />}
                        leadTone="slate"
                        title={bandTitle(b)}
                        subtitle={
                          t('mobile.rpd.band_meta', {
                            count,
                            height: Math.round(b.height_pt),
                            defaultValue: `${count} elements · ${Math.round(b.height_pt)} pt`,
                          }) + (summary ? ` · ${summary}` : '')
                        }
                      />
                    );
                  })}
                </MobileContentCard>
              </>
            ) : detail ? (
              <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.rpd.no_bands', 'No bands defined yet')}</div></MobileContentCard>
            ) : null}

            <div className="m-fieldcard">
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.rpd.desktop_title', 'Design & run on desktop')}</div>
                  <div className="m-formrow-s">{t('mobile.rpd.desktop_hint', 'Editing bands and elements, and rendering a live preview, are available in the desktop Report Designer.')}</div>
                </span>
                <span className="m-lead m-l-slate" aria-hidden><Monitor size={16} /></span>
              </div>
            </div>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" onClick={closeSheet}>
                <X size={16} />
                {t('common.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
